import { bootstrapApplication } from '@angular/platform-browser';
import { App } from './app/app';
import { appConfig } from './app/app.config';
import { appConfigTest } from './app/app.config.test';

// Initialize theme immediately before Angular bootstraps
// This ensures theme is applied even in new tabs before the app loads
function initializeTheme(): void {
  if (typeof document === 'undefined') return;

  const saved = localStorage.getItem('app-theme') as 'light' | 'dark' | null;
  if (saved === 'light' || saved === 'dark') {
    document.documentElement.setAttribute('data-theme', saved);
  } else {
    const prefersDark =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme = prefersDark ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('app-theme', theme);
  }
  // Match ThemeService — native UA chrome follows the app theme.
  const applied = document.documentElement.getAttribute('data-theme');
  if (applied === 'light' || applied === 'dark') {
    document.documentElement.style.colorScheme = applied;
  }
}

// Initialize theme synchronously before Angular bootstraps
initializeTheme();

// Don't configure Amplify here - it will be configured by APP_INITIALIZER
// This prevents the "UserPool not configured" error

// Use test configuration when running tests (Karma runs on port 9876 by default)
const isTest =
  typeof window !== 'undefined' &&
  (window.location.href.includes('localhost:9876') ||
    window.location.href.includes('localhost:9877') ||
    window.location.href.includes('context.html'));
const config = isTest ? appConfigTest : appConfig;

if (isTest) {
  console.log('🧪 Running in test environment - using appConfigTest');
} else {
  console.log('🚀 Running in production environment - using appConfig');
}

/**
 * Bootstrap-failure UI.
 *
 * When `bootstrapApplication(...)` rejects (env-config failed,
 * Amplify configure threw, a polyfill is missing, an
 * APP_INITIALIZER errored), Angular never produces a view and the
 * pre-boot loader inside `<app-root>` would spin forever. The
 * `.catch()` here is the **documented Angular pattern** for that
 * case — deterministic, no timing race with a `setTimeout` in
 * `index.html`. We swap `<app-root>`'s children for a minimal error
 * panel with a Reload button that calls the cache-busting reload
 * helper from `index.html`.
 *
 * The Angular-side stuck-after-bootstrap path (Amplify call hangs,
 * `/whoami` hangs, first-nav errors out) is handled by
 * `BootHealthService` once Angular has booted — see `app.ts`.
 */
function renderBootstrapError(err: unknown): void {
  console.error('[bootstrap] failed', err);
  const root = document.querySelector('app-root');
  if (!root) return;

  // Brand colours hard-coded for the same reason `index.html`'s
  // pre-boot loader hard-codes them: if bootstrap failed before the
  // global stylesheet was parsed (e.g. a styles-XXX.css 404), CSS
  // variables aren't resolvable. Tokens here mirror
  // `var(--primary-color)` / `var(--bg-main)` / `var(--text-primary)`
  // in `styles/variables.css` for both themes.
  const theme = document.documentElement.getAttribute('data-theme') || 'light';
  const isDark = theme === 'dark';
  // Kept in lock-step with `--bg-main` / `--text-primary` / `--primary-color`
  // in `styles/variables.css`. If those tokens shift and these constants
  // don't, the pre-boot splash flashes a mismatched colour before the app
  // paints.
  const bg = isDark ? '#0e0f14' : '#ececf0';
  const fg = isDark ? '#f5f5f7' : '#120c2b';
  const accent = isDark ? '#a888ff' : '#441752';
  const onAccent = isDark ? '#0e0f14' : '#ffffff';

  root.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      gap: 16px;
      padding: 24px;
      background: ${bg};
      color: ${fg};
      font: 14px/1.5 system-ui, -apple-system, 'Segoe UI', sans-serif;
      text-align: center;
    ">
      <div style="font-size: 16px; font-weight: 600;">
        We couldn't start the app.
      </div>
      <div style="opacity: 0.75; max-width: 360px;">
        This usually means a temporary network issue. Please try reloading the page.
      </div>
      <button type="button" id="bootstrapErrorReload" style="
        padding: 10px 20px;
        border: none;
        border-radius: 6px;
        background: ${accent};
        color: ${onAccent};
        font: 600 14px/1 system-ui, -apple-system, 'Segoe UI', sans-serif;
        cursor: pointer;
      ">Reload</button>
    </div>
  `;

  document.getElementById('bootstrapErrorReload')?.addEventListener('click', () => {
    const reload = (window as unknown as { __reloadWithFreshShell?: () => void })
      .__reloadWithFreshShell;
    if (typeof reload === 'function') {
      reload();
    } else {
      window.location.reload();
    }
  });
}

bootstrapApplication(App, config).catch(renderBootstrapError);
